import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { HITController } from '../../common/js/HITController';
import { Assumptions } from './static/Assumptions';
import { BusinessRules } from './registries/BusinessRules';

@supertypeClass({ toClient: true, toServer: true })
export class ProductRulesController extends AppController {
  @property({ isLocal: true })
  productName: string;

  @property({ isLocal: true })
  minimumAge: number;

  @property({ isLocal: true })
  maximumAge: number;

  @property({ isLocal: true })
  cutOffAge: number;

  @property({ isLocal: true })
  minimumFace: number;

  @property({ isLocal: true })
  maximumFace: number;

  @property({ isLocal: true })
  cutOffFace: number;

  @property({ isLocal: true })
  minimumConvertibleAge: number;

  @property({ isLocal: true })
  maximumConvertibleAge: number;

  @property({ isLocal: true })
  cutOffConvertibleAge: number;

  @property({ type: Array, toServer: false, getType: () => String })
  termValues: Array<string>;

  @property()
  validationError: any = {
    errorType: { toServer: false },
    errorMessage: { toServer: false },
    isFurtherStepAvailable: { toServer: false },
    furtherAction: {},
    furtherActionText: { toServer: false },
  };

  @property({ toServer: false })
  metadata;

  @property({ getType: () => HITController })
  controller: HITController;

  @property({ toServer: false })
  channelAssumptions: any = {};

  constructor() {
    super();
  }

  static new(controller) {
    return new ProductRulesController().init(controller);
  }

  init(controller) {
    this.controller = controller;
    return this;
  }

  productRulesControllerTrigger() {
    if (this.controller['productRulesControllerTrigger']) {
      this.controller['productRulesControllerTrigger']();
    }
  }

  loadRules(channel: string) {
    this.channelAssumptions = Assumptions.channel(channel);
    this.productName = this.getProductName();

    this.metadata = BusinessRules[this.channelAssumptions.settings.channel.code];

    this.minimumAge = parseInt(this.metadata['AgeRange'].value1);
    this.maximumAge = parseInt(this.metadata['AgeRange'].value3);
    this.cutOffAge = parseInt(this.metadata['AgeRange'].value2);

    this.minimumFace = parseInt(this.metadata['FaceLimit'].value1);
    this.maximumFace = parseInt(this.metadata['FaceLimit'].value3);
    this.cutOffFace = parseInt(this.metadata['FaceLimit'].value2);

    this.minimumConvertibleAge = parseInt(this.metadata['ConvertibilityAgeLimit'].value1);
    this.maximumConvertibleAge = parseInt(this.metadata['ConvertibilityAgeLimit'].value3);
    this.cutOffConvertibleAge = parseInt(this.metadata['ConvertibilityAgeLimit'].value2);

    this.termValues = [];

    for (const value in this.metadata['AllowedTerm']) {
      this.termValues.push(this.metadata['AllowedTerm'][value]);
    }
  }

  isValid(customer) {
    const needsFace = customer.capitalNeeds.getFace(false);

    if (!this.controller.isProductSoldIn(customer.primaryCustomer.address.state)) {
      this.validationError.errorMessage = this.productName + ' is not available in your state';
      this.validationError.isFurtherStepAvailable = true;

      this.validationError.furtherAction = function () {
        this.controller.route.public.dialog.enroll();
      };

      this.validationError.furtherActionText = 'Sign up';
      this.validationError.furtherActionPostText = 'to be notified when it is.';

      return false;
    } else if (
      this.controller.isProductSoldIn(customer.primaryCustomer.address.state) &&
      needsFace > this.maximumFace &&
      customer.primaryCustomer.person.age <= this.maximumAge - 1
    ) {
      this.validationError.errorMessage = this.productName + ' is not available above $2 million.';
      this.validationError.isFurtherStepAvailable = true;

      this.validationError.furtherAction = function () {
        this.controller.route.public.pickorquick();
      };

      this.validationError.furtherActionText = 'Want a $2 million Haven quote?';
      this.validationError.furtherActionPostText = '';

      return false;
    } else if (
      this.controller.isProductSoldIn(customer.primaryCustomer.address.state) &&
      needsFace > this.cutOffFace &&
      customer.primaryCustomer.person.age > this.cutOffAge &&
      customer.primaryCustomer.person.age <= this.maximumAge - 1
    ) {
      this.validationError.errorMessage = this.productName + ' is not available above $1 million for this age';
      this.validationError.isFurtherStepAvailable = true;

      this.validationError.furtherAction = function () {
        customer.primaryCustomer.overrideAmount = this.cutOffFace;
        this.controller.route.public.pickorquick();
      };

      this.validationError.furtherActionText = 'Want a $1 million Haven quote?';
      this.validationError.furtherActionPostText = '';

      return false;
    } else if (
      this.controller.isProductSoldIn(customer.primaryCustomer.address.state) &&
      needsFace < this.minimumFace &&
      customer.primaryCustomer.person.age <= this.maximumAge - 1
    ) {
      this.validationError.errorMessage = this.productName + ' is not available under $100,000';
      this.validationError.isFurtherStepAvailable = true;

      this.validationError.furtherAction = function () {
        customer.primaryCustomer.overrideAmount = this.minimumFace;
        this.controller.route.public.pickorquick();
      };

      this.validationError.furtherActionText = 'Want a Haven quote for $100,000?';
      this.validationError.furtherActionPostText = '';

      return false;
    } else if (
      this.controller.isProductSoldIn(customer.primaryCustomer.address.state) &&
      (customer.primaryCustomer.person.age > this.maximumAge - 1 ||
        customer.primaryCustomer.person.age < this.minimumAge)
    ) {
      const message =
        this.productName +
        ' is available for people of ages ' +
        this.minimumAge +
        '-' +
        (this.maximumAge - 1) +
        ', only.';

      this.validationError.errorMessage = message;
      this.validationError.isFurtherStepAvailable = false;
      this.validationError.furtherAction = function () {};
      this.validationError.furtherActionText = '';
      this.validationError.furtherActionPostText = '';

      return false;
    }

    return true;
  }

  getProductName() {
    const brand = this.channelAssumptions.settings.channel.emailBranding.name || 'MassMutual';
    const product = this.channelAssumptions.settings.product.type.name || 'Term';

    return brand + ' ' + product;
  }
}
